import { useEffect } from 'react';
import { useRouter } from 'next/router';

/**
 * Hook to preserve UTM parameters from document.referrer
 *
 * This hook handles three scenarios:
 * 1. When the page loads without query params but has a referrer with UTM params,
 *    it extracts them from the referrer and updates the URL (shallow, no reload)
 * 2. When navigating between signup/login using page elements (useUrlWithParams),
 *    the existing URL params are already preserved by Next.js router
 * 3. If the page already has query params and there's no referrer, leave them as-is
 */
export const usePreserveUtmParams = () => {
  const router = useRouter();

  useEffect(() => {
    // Only run once on mount
    if (!router.isReady) return;

    const currentUrl = new URL(window.location.href);
    const currentParams = currentUrl.searchParams;

    // List of parameters we want to preserve
    const paramsToPreserve = ['from', 'recovery_path', 'utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content'];

    // Check if we already have any query parameters
    const hasExistingParams = paramsToPreserve.some((param) => currentParams.has(param));

    // If we already have parameters, don't do anything (Scenario 3)
    if (hasExistingParams) {
      return;
    }

    // Check if we have a referrer
    const referrer = document.referrer;
    if (!referrer) {
      return;
    }

    // Try to parse the referrer URL
    let referrerUrl: URL;
    try {
      referrerUrl = new URL(referrer);
    } catch {
      // Invalid referrer URL, ignore
      return;
    }

    // Extract UTM parameters from the referrer
    const paramsToAdd: Record<string, string> = {};
    paramsToPreserve.forEach((param) => {
      const value = referrerUrl.searchParams.get(param);
      if (value) {
        paramsToAdd[param] = value;
      }
    });

    // If we found any parameters to add, update the URL
    if (Object.keys(paramsToAdd).length > 0) {
      // Shallow update the URL without reloading the page
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, ...paramsToAdd },
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.isReady]); // Only depend on router.isReady to run once
};
